import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, defineAsyncComponent, PropType, getCurrentInstance } from 'vue';
import VueI18n from 'vue-i18n';

// Error message can be targeted at specific fields or all three fields.
type ErrorMessageObject = {
  day?: VueI18n.TranslateResult;
  month?: VueI18n.TranslateResult;
  year?: VueI18n.TranslateResult;
  all?: VueI18n.TranslateResult;
}

// Components

export default /*#__PURE__*/_defineComponent({
  __name: 'date-input',
  props: {
  value: {
    // String is only used here so that we can accept an emptry string as an initial value
    // Date should always be preferred
    type: [Date, String] as PropType<Date | string>,
    default: '',
  },
  label: {
    type: [String, Object] as PropType<VueI18n.TranslateResult | string>,
    default: (getCurrentInstance()?.proxy as Vue)?.$t('components.dateTimePicker.date') as string ?? 'Date',
  },
  locale: {
    type: String,
    default: 'en-GB',
  },
  errorMessage: {
    type: Object as PropType<ErrorMessageObject>,
    default: () => {},
  },
},
  emits: ["input", "blur"],
  setup(__props, { emit }: { emit: ({
  (event: 'input', value: Date): void;
  (blur: 'blur'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const IconError = defineAsyncComponent(
  () => import('@white-label-icon/icon-error')
);

// Instance proxy for translations
const proxy = getCurrentInstance()?.proxy as Vue;

// Props


// Emits


// Internal state so that we can sanitize and add leading zeros
const inputOne = ref('');
const inputTwo = ref('');
const inputThree = ref('');

// If the user has blurred the year input, we can start validating the date
const yearHasBeenBlurred = ref(false);

// Check if current locale uses day first or month first format
const dayFirst = computed(() => {
  const localesWithMonthFirst = ['en-US'];
  return !localesWithMonthFirst.includes(props.locale);
});

// Get date parts for use in HTML attributes
const dateParts = computed(() => {
  return dayFirst.value ? ['day', 'month', 'year'] : ['month', 'day', 'year'];
});

// Number values for day, month, and year
const day = computed(() => {
  return dayFirst.value ? +inputOne.value : +inputTwo.value;
})
const month = computed(() => {
  return dayFirst.value ? +inputTwo.value : +inputOne.value;
})
const year = computed(() => {
  return +inputThree.value;
});

// Generated Date object based on current input values
const date = computed(() => {
  return new Date(
    year.value,
    month.value - 1,
    day.value
  );
});

// Generate error message if the date is invalid
const internalErrorMessages = computed(() => {
  // Don't validate until user has attempted to enter the year
  if (!yearHasBeenBlurred.value) return null;

  if (day.value > 31 || day.value < 1) {
    return { day: proxy.$t('UI.dateInput.formErrors.invalidDay') };
  } else if (month.value > 12 || month.value < 1) {
    return { month: proxy.$t('UI.dateInput.formErrors.invalidMonth') };
  } else if (year.value < 1000 || year.value > 9999) {
    return { year: proxy.$t('UI.dateInput.formErrors.invalidYear') };
  } else if (day.value && month.value && year.value) {
    if (date.value.getDate() !== day.value || date.value.getMonth() !== month.value - 1 || date.value.getFullYear() !== year.value) {
      return { all: proxy.$t('UI.dateInput.formErrors.invalidDate')};
    }
  }
  return null;
});

// Pull first error message from internal errors
const internalErrorMessage = computed(() => {
  if (internalErrorMessages.value) {
    const errorKeys = Object.keys(internalErrorMessages.value) as (keyof ErrorMessageObject)[];
    if (errorKeys.length > 0) {
      return internalErrorMessages.value?.[errorKeys[0]];
    }
  }
  return '';
});

// Pull first error message from external errors
const externalErrorMessage = computed(() => {
  const keys = Object.keys(props.errorMessage) as (keyof ErrorMessageObject)[];
  if (keys.length === 0) return '';
  return props.errorMessage[keys[0]] ?? '';
});

// Show error message based on the field
const showError = (field: string): boolean => {
  if (props.errorMessage?.all || internalErrorMessages?.value?.all) return true;
  if (field === 'day') {
    return !!(internalErrorMessages.value?.day
      ?? props.errorMessage?.day);
  } else if (field === 'month') {
    return !!(internalErrorMessages.value?.month
      ?? props.errorMessage?.month);
  } else {
    return !!(internalErrorMessages.value?.year
      ?? props.errorMessage?.year);
  }
};

// Replace non-numeric characters
const sanitizeNumbers = (value: string) => {
  return value.replace(/\D/g, '')
};

// Add a leading zero to single-digit values
const addLeadingZero = (value: string) => {
  if (value.length === 1 && value !== '0') {
    return `0${value}`;
  }
  return value;
};

// Remove non-numeric characters, and emit the resulting Date
const onInput = (value: string, field: 'one' | 'two' | 'three') => {
  if (field === 'one') {
    inputOne.value = sanitizeNumbers(value);
  } else if (field === 'two') {
    inputTwo.value = sanitizeNumbers(value);
  } else if (field === 'three') {
    inputThree.value = sanitizeNumbers(value);
  }

  emit('input', date.value);
}

return { __sfc: true,IconError, proxy, props, emit, inputOne, inputTwo, inputThree, yearHasBeenBlurred, dayFirst, dateParts, day, month, year, date, internalErrorMessages, internalErrorMessage, externalErrorMessage, showError, sanitizeNumbers, addLeadingZero, onInput }
}

})